/**
 * ユーティリティ関数集
 */

/**
 * 実行時間をフォーマット
 * - 60秒未満: "X.X秒"（小数点以下1桁）
 * - 60秒以上: "M:SS"（分:秒）
 *
 * @param durationMs 実行時間（ミリ秒）
 * @returns フォーマット済みの文字列
 */
export function formatDuration(durationMs: number): string {
  // 負の値は 0 として扱う
  if (durationMs < 0) {
    return '0.0秒';
  }

  const totalSeconds = durationMs / 1000;

  // 60秒未満は「X.X秒」形式
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}秒`;
  }

  // 60秒以上は「M:SS」形式
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * 日付の差分（日数）を計算
 */
function getDiffDays(date: Date, now: Date): number {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((nowOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * 日時をフォーマット（省略形式）
 *
 * デスクトップアプリのタスク履歴（date-utils.formatDateTime）と表記を揃える。
 * - 今日: "今日 HH:mm"
 * - 昨日: "昨日 HH:mm"
 * - 7日以内: "N日前"
 * - それ以外: "YYYY年M月D日"
 *
 * @param timestamp ISO 8601 形式のタイムスタンプ、またはエポックミリ秒
 * @param now 基準時刻（省略時は現在時刻。テスト用）
 * @returns フォーマット済みの文字列
 */
export function formatDateTime(timestamp: number | string, now: Date = new Date()): string {
  const date = new Date(timestamp);
  const diffDays = getDiffDays(date, now);
  const time = date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) {
    return `今日 ${time}`;
  }
  if (diffDays === 1) {
    return `昨日 ${time}`;
  }
  if (diffDays < 7) {
    return `${diffDays}日前`;
  }
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * 日時を省略なしの完全形式でフォーマット
 *
 * formatDateTime は「今日」「N日前」のように省略するため、正確な日時を補足したい箇所
 * （ツールチップなど）で使う。
 * 例: "2026年9月4日 09:05:30"
 *
 * @param timestamp ISO 8601 形式のタイムスタンプ、またはエポックミリ秒
 * @returns フォーマット済みの文字列
 */
export function formatFullDateTime(timestamp: number | string): string {
  return new Date(timestamp).toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
